import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Cart, CartItem } from '@features/cart/interfaces/cart.interface';
import { CartService } from '@features/cart/services/cart.service';
import { DiscountHelperService } from '@shared/services/discount-helper.service';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  CheckoutCartItem,
  CheckoutForm,
  CheckoutShop,
} from '../interfaces/checkout.interface';

const apiUrl = environment.apiUrl + 'user/checkout';
const SHIPPING_COST = 5;

@Injectable({
  providedIn: 'root',
})
export class CheckoutService {
  checkoutFormData: BehaviorSubject<CheckoutForm> = new BehaviorSubject(
    {} as CheckoutForm
  );
  constructor(
    private http: HttpClient,
    private discountHelper: DiscountHelperService,
    private cartService: CartService
  ) {}

  getCartProductShop(cartItems: CartItem[]) {
    let checkoutShops: CheckoutShop[] = [];
    cartItems.forEach((item: CartItem) => {
      let shop = checkoutShops.find((shop: any) => shop.id == item.shop_id);
      if (shop) {
        shop.items.push(item);
        shop.total += item.totalPrice;
        shop.totalQuantity += item.quantity;
        shop.total_weight += item.totalWeight;
      } else {
        checkoutShops.push({
          id: item.shop_id,
          shop_name: item.shop_name,
          items: [item],
          totalQuantity: item.quantity,
          shippingCost: SHIPPING_COST,
          note: '',
          voucher_code: '',
          subtotal: this.getShopSubtotal([item]),
          total: item.totalPrice + SHIPPING_COST,
          total_weight: item.totalWeight,
        } as CheckoutShop);
      }
    });
    return checkoutShops;
  }
  setCheckoutForm(cart: Cart) {
    let checkoutForm = this.checkoutFormData.value;
    checkoutForm.shops = this.getCartProductShop(cart.cartItems);
    checkoutForm.cart_items = this.getCheckoutCartItems(cart.cartItems);
    checkoutForm.subtotal = this.getShopSubtotal(cart.cartItems);
    checkoutForm.total_shipping_fee = checkoutForm.shops.reduce(
      (a: number, b: CheckoutShop) => a + b.shippingCost,
      0
    );
    checkoutForm.grand_total = this.getCheckoutGrandTotal(checkoutForm);
    checkoutForm.happy_pay_partial_payment = false;
    checkoutForm.note = '';
    checkoutForm.promo_code = '';
    checkoutForm.promo_code_value = 0;
    console.log(checkoutForm);
    this.checkoutFormData.next(checkoutForm);
  }
  resetCheckoutForm() {
    this.checkoutFormData.next({} as CheckoutForm);
  }
  getCheckoutCartItems(cartItem: CartItem[]): CheckoutCartItem[] {
    return cartItem.map((item: CartItem) => {
      return {
        id: item.id,
        quantity: item.quantity,
        price: item.totalPrice,
      };
    });
  }

  get getCheckoutFormValue(): Observable<CheckoutForm> {
    return this.checkoutFormData.asObservable();
  }
  setCheckoutAddress(addressId: string) {
    let checkoutForm = this.checkoutFormData.value;
    checkoutForm.user_address_id = addressId;
    this.checkoutFormData.next(checkoutForm);
  }
  setCheckoutShopVoucher(voucher: any, shopId: any) {
    let checkoutForm = this.checkoutFormData.value;
    let shop = checkoutForm.shops.find((shop: any) => shop.id == shopId);
    if (shop) {
      shop.voucher_code = voucher.code;
      if (voucher.discount_type == 0) {
        let shopVoucherTotal =
          (Number(voucher.amount) * this.getShopSubtotal(shop.items)) / 100;
        if (shopVoucherTotal <= voucher.amount_limit) {
          shop.voucher_value = shopVoucherTotal;
        } else {
          shop.voucher_value = voucher.amount_limit;
        }
      } else {
        if (Number(voucher.amount) <= shop.subtotal) {
          shop.voucher_value = Number(voucher.amount);
        } else {
          shop.voucher_value = this.getShopSubtotal(shop.items);
        }
      }
      shop.total = this.getShopTotal(shop);
    }
    this.setCheckoutSummary();
    this.checkoutFormData.next(checkoutForm);
  }
  setCheckoutBeautybookVoucher(voucher: any) {
    let checkoutForm = this.checkoutFormData.value;
    if (voucher) {
      checkoutForm.promo_code = voucher.code;
      if (voucher.discount_type == 0) {
        checkoutForm.promo_code_value =
          this.discountHelper.discountValueRounding(
            (Number(voucher.discount_value) *
              (checkoutForm.subtotal - checkoutForm.total_shipping_fee)) /
              100
          );
      } else {
        checkoutForm.promo_code_value = Number(voucher.discount_value);
      }
    } else {
      checkoutForm.promo_code = null;
      checkoutForm.promo_code_value = 0;
    }
    this.setCheckoutSummary();
    this.checkoutFormData.next(checkoutForm);
  }
  setPaymentMethod(paymentMethod: any) {
    let checkoutForm = this.checkoutFormData.value;
    checkoutForm.payment_method = paymentMethod;
    this.checkoutFormData.next(checkoutForm);
  }
  setHappyPayBalance(balance: any) {
    let checkoutForm = this.checkoutFormData.value;
    checkoutForm.happy_pay_partial_amount = balance;
    this.checkoutFormData.next(checkoutForm);
  }
  setBankAccountId(bankAccountId: any) {
    let checkoutForm = this.checkoutFormData.value;
    checkoutForm.happy_beauty_bank_account_id = bankAccountId;
    this.checkoutFormData.next(checkoutForm);
  }
  setCheckoutSummary() {
    let checkoutForm = this.checkoutFormData.value;
    checkoutForm.subtotal = this.getCheckoutSubtotal(checkoutForm.shops);
    checkoutForm.total_shipping_fee = this.getTotalShippingFee(
      checkoutForm.shops
    );
    checkoutForm.grand_total = this.getCheckoutGrandTotal(checkoutForm);
    this.checkoutFormData.next(checkoutForm);
  }

  getTotalShippingFee(shops: CheckoutShop[]): number {
    let totalShippingFee = shops.reduce(
      (a: number, b: CheckoutShop) => a + b.shippingCost,
      0
    );
    return totalShippingFee;
  }
  getShopSubtotal(items: any): number {
    return (
      items.reduce((a: number, b: CartItem) => a + b.totalPrice, 0) +
      SHIPPING_COST
    );
  }
  getShopTotal(shop: CheckoutShop): number {
    let subTotal = this.getShopSubtotal(shop.items);
    let total = subTotal - (shop.voucher_value || 0) + shop.shippingCost;
    return total;
  }
  getCheckoutSubtotal(shops: CheckoutShop[]): number {
    return shops.reduce((a: number, b: CheckoutShop) => a + b.total, 0);
  }
  getCheckoutGrandTotal(checkoutForm: CheckoutForm): number {
    return checkoutForm.subtotal - (checkoutForm.promo_code_value || 0);
  }

  checkout(checkoutData: CheckoutForm) {
    return this.http.post(apiUrl, checkoutData).pipe(
      map((res: any) => {
        this.cartService.syncCart();
        return res;
      })
    );
  }

  private handleError(errorRes: HttpErrorResponse) {}

  private removeAllDataState() {}
}
